<script setup lang="ts">
    import { type FormKitOptionsProp } from '@formkit/inputs';
    import { SizeEnum } from '@shared/constants/form-style';

    const props = defineProps<{
        modelValue?: boolean
        id?: string,
        name: string
        label?: string
        validation?: string | [rule: string, ...args: unknown[]][]
        validationVisibility?: 'submit' | 'live' | 'blur' | 'dirty' | undefined
        options?: FormKitOptionsProp
        help?: string
        size?: SizeEnum.SMALL | SizeEnum.MEDIUM | SizeEnum.LARGE | SizeEnum.XLARGE
        disabled?: boolean
        labelHidden?: boolean
        value?: boolean
        readonly?: boolean
        ignore?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<(event: 'update:modelValue' | 'input', value: boolean | undefined) => void>();

    const valueModel = ref(props.modelValue ?? false);

    const isRequired = computed(() =>
        Array.isArray(props.validation) ? props.validation[0].includes('required') : props.validation?.includes('required'));

    // Sync prop modelValue with internal valueModel
    watch(() => props.modelValue, newValue => {
        if (valueModel.value !== newValue) {
            valueModel.value = newValue;
        }
    });

    // Emit updated value when valueModel changes
    watch(valueModel, newValue => {
        if (props.modelValue !== newValue) {
            emit('update:modelValue', newValue);
        }
    });
</script>

<template>
    <div
        :data-required="(isRequired as Boolean && !options)"
        class="checkbox"
        data-constell="constell-checkbox"
    >
        <!-- Has multiple checkboxes -->
        <FormKit
            v-if="options"
            :id="id ?? name ?? 'checkbox'"
            v-model="valueModel"
            type="checkbox"
            :label="label"
            :name="name"
            :options="options"
            :validation="validation"
            :validation-visibility="validationVisibility"
            :help="help"
            :wrapper-class="{'checkbox__validation': validation}"
            :inner-class="size ?? SizeEnum.MEDIUM"
            :disabled="disabled"
            :legend-class="labelHidden ? 'formkit-legend--hidden' : ''"
            :value="value"
            :readonly="readonly"
            :ignore="ignore"
            decorator-icon="check"
            data-constell="constell-checkbox-input"
            @input="emit('input', $event)"
        />

        <!-- Just one checkbox -->
        <FormKit
            v-else
            :id="id"
            v-model="valueModel"
            type="checkbox"
            :value="value"
            :label="label"
            :name="name"
            :validation="validation"
            :validation-visibility="validationVisibility"
            :help="help"
            :wrapper-class="{'checkbox__validation': validation}"
            :outer-class="readonly === true ? 'readonly' : null"
            :inner-class="size ?? SizeEnum.MEDIUM"
            :disabled="disabled"
            :readonly="readonly"
            :label-class="labelHidden ? 'formkit-label--hidden' : ''"
            :data-readonly="readonly"
            :ignore="ignore"
            decorator-icon="check"
            data-constell="constell-checkbox-input-2"
        />
    </div>
</template>

<style scoped lang="scss">
    .checkbox {
        &[data-required=true] {
            :deep(.formkit-label){
                &::after {
                    color: var(--text-secondary);
                    content:" *";
                }
            }
        }

        :deep(.formkit-outer) {
            --checkbox-small: var(--size-2);
            --checkbox-medium: var(--size-3);
            --checkbox-large: var(--size-4);
            --checkbox-xlarge: var(--size-5);

            &[data-type=checkbox] {
                .formkit-wrapper {
                    display: flex;
                    align-items: center;

                    .sm .checkbox__input,
                    .sm .formkit-decorator,
                    .sm .formkit-decorator-icon > svg {
                        block-size: var(--checkbox-small);
                        inline-size: var(--checkbox-small);
                    }

                    .md .checkbox__input,
                    .md .formkit-decorator,
                    .md .formkit-decorator-icon > svg {
                        block-size: var(--checkbox-medium);
                        inline-size: var(--checkbox-medium);
                    }

                    .lg .checkbox__input,
                    .lg .formkit-decorator,
                    .lg .formkit-decorator-icon > svg {
                        block-size: var(--checkbox-large);
                        inline-size: var(--checkbox-large);
                    }

                    .xl .checkbox__input,
                    .xl .formkit-decorator,
                    .xl .formkit-decorator-icon > svg {
                        block-size: var(--checkbox-xlarge);
                        inline-size: var(--checkbox-xlarge);
                    }

                    input {
                        display: none;
                    }

                    .formkit-decorator {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 1px solid var(--border-quaternary);
                        border-radius: var(--border-radius-xs);
                        background-color: var(--background-primary);
                        block-size: var(--checkbox-small);
                        cursor: pointer;
                        inline-size: var(--checkbox-small);

                        &:hover, &:focus {
                            background-color: var(--background-primary-highlight);
                        }

                    }

                    .formkit-decorator-icon {
                        display: none;
                        align-items: center;
                        justify-content: center;
                    }

                    /* stylelint-disable-next-line no-descending-specificity */
                    .formkit-label {
                        color: var(--text-primary);
                        font-size: var(--font-regular);
                        margin-inline-start: var(--spacing-02);
                    }

                    // Positive validation style checkbox
                    // &.checkbox__validation[data-checked] .formkit-decorator {
                        // background-color: var(--background-success-1);
                        // border-color: var(--border-success);
                    // }

                    // Checkbox checked style
                    &[data-checked] .formkit-decorator {
                        border-color: var(--border-quaternary);
                        background: var(--background-primary-invert-highlight);

                    }

                    &[data-checked] .formkit-decorator-icon {
                        display: flex;
                        color: var(--text-primary-invert);
                    }
                }

                // Negative validation style for checkbox
                &[data-invalid] .formkit-wrapper .formkit-decorator {
                    border: 1px solid var(--border-negative);
                }

                @include min-width(md) {
                    .formkit-decorator {
                        block-size: var(--checkbox-medium);
                        inline-size: var(--checkbox-medium);
                    }
                }

                @include min-width(lg) {
                    .formkit-decorator {
                        block-size: var(--checkbox-large);
                        inline-size: var(--checkbox-large);
                    }
                }
            }

            &[data-disabled=true] {
                /* stylelint-disable-next-line no-descending-specificity */
                &[data-type=checkbox] {
                    /* stylelint-disable-next-line no-descending-specificity */
                    .formkit-decorator {
                        border-color: var(--border-disabled-1);
                        background-color: var(--background-disabled-2);
                        cursor: not-allowed;

                        &:hover, &:focus {
                            background-color: var(--background-disabled-2);
                        }
                    }

                    .formkit-label {
                        color: var(--text-disabled-1);
                    }
                }
            }

            &.readonly {
                pointer-events: none;
            }
        }
    }

    .checkbox[data-required=true] :deep(.formkit-outer) {
        .formkit-label::after {
            color: var(--text-secondary);
            content:" *";
        }
    }
</style>
